/**
 * Search Page Script
 * @description Script para página de busca
 * @version 1.0.0
 * @author Irroba Platform
 */

// Importar módulos globais comuns
import LazyLoading from '../functions/lazy-loading.js';
import { newSearch } from '../functions/search.js';
import { generateTemplateContent, parseToHtml, replaceString } from '../functions/html_element.js';
import { setCacheData, getCacheData } from '../functions/cache.js';
import { initGlobalComponents } from '../modules/global-components.js';
import { initProductListsNearViewport } from '../modules/product_list_module.js';

/**
 * Inicialização da página de busca
 */
const initSearchPage = () => {
    console.log('🚀 Inicializando página de busca...');

    try {
        // Inicializar componentes comuns
        initGlobalComponents();
        initProductListsNearViewport();
        initLazyLoading();

        // Funcionalidades específicas da busca
        initSearchForm();
        initFilters();
        initSorting();
        initPagination();
        initSearchSuggestions();
        
        console.log('✅ Página de busca inicializada com sucesso!');
    } catch (error) {
        console.error('❌ Erro ao inicializar página de busca:', error);
    }
};

/**
 * Inicializa Lazy Loading de imagens
 */
const initLazyLoading = () => {
    try {
        new LazyLoading({
            selector: 'img[data-src]',
            rootMargin: '50px'
        });
        console.log('✅ Lazy Loading inicializado');
    } catch (error) {
        console.warn('Erro ao inicializar Lazy Loading:', error);
    }
};

/**
 * Inicializa formulário de busca
 */
const initSearchForm = () => {
    const searchForm = document.querySelector('[data-search-form]');
    const searchInput = document.querySelector('[data-search-input]');
    
    if (searchForm && searchInput) {
        // Auto focus no input de busca
        searchInput.focus();
        
        searchForm.addEventListener('submit', function(e) {
            const searchTerm = searchInput.value.trim();
            
            if (searchTerm.length < 2) {
                e.preventDefault();
                alert('Digite pelo menos 2 caracteres para buscar');
                return;
            }
            
            console.log('🔍 Buscando por:', searchTerm);
        });
        
        // Limpar busca
        const clearBtn = document.querySelector('[data-search-clear]');
        if (clearBtn) {
            clearBtn.addEventListener('click', function() {
                searchInput.value = '';
                searchInput.focus();
                console.log('🗑️ Busca limpa');
            });
        }
    }
};

/**
 * Inicializa sugestões de busca
 */
const initSearchSuggestions = () => {
    const suggestionsContainer = document.querySelector('[data-search-suggestions]');
    const searchInput = document.querySelector('[data-search-input]');
    
    if (!searchInput || !suggestionsContainer) return;
    
    let debounceTimer;
    
    searchInput.addEventListener('input', function() {
        clearTimeout(debounceTimer);
        
        const term = this.value.trim();
        
        if (term.length < 2) {
            suggestionsContainer.classList.remove('active');
            return;
        }
        
        debounceTimer = setTimeout(() => {
            console.log('🔍 Buscando sugestões para:', term);
            // Implementar busca de sugestões via AJAX
            fetchSearchSuggestions(term);
        }, 300);
    });
    
    // Fechar sugestões ao clicar fora
    document.addEventListener('click', function(e) {
        if (!suggestionsContainer.contains(e.target) && e.target !== searchInput) {
            suggestionsContainer.classList.remove('active');
        }
    });
};

/**
 * Busca sugestões via AJAX
 */
const fetchSearchSuggestions = (term) => {
    // Implementar requisição AJAX para buscar sugestões
    console.log('Buscando sugestões para:', term);
    
    /*
    fetch(`/index.php?route=product/search/suggest&search=${encodeURIComponent(term)}`)
        .then(response => response.json())
        .then(data => {
            displaySearchSuggestions(data);
        })
        .catch(error => {
            console.error('Erro ao buscar sugestões:', error);
        });
    */
};

/**
 * Exibe sugestões de busca
 */
const displaySearchSuggestions = (suggestions) => {
    const suggestionsContainer = document.querySelector('[data-search-suggestions]');
    
    if (!suggestionsContainer) return;
    
    // Limpar sugestões anteriores
    suggestionsContainer.innerHTML = '';
    
    if (suggestions.length === 0) {
        suggestionsContainer.classList.remove('active');
        return;
    }
    
    // Criar lista de sugestões
    const list = document.createElement('ul');
    
    suggestions.forEach(suggestion => {
        const item = document.createElement('li');
        item.innerHTML = `<a href="${suggestion.url}">${suggestion.name}</a>`;
        list.appendChild(item);
    });
    
    suggestionsContainer.appendChild(list);
    suggestionsContainer.classList.add('active');
};

/**
 * Inicializa sistema de filtros
 */
const initFilters = () => {
    const filterToggles = document.querySelectorAll('[data-filter-toggle]');
    
    filterToggles.forEach(toggle => {
        toggle.addEventListener('click', function() {
            const filterPanel = document.querySelector('[data-filter-panel]');
            if (filterPanel) {
                filterPanel.classList.toggle('active');
                console.log('📊 Filtros alternados');
            }
        });
    });

    // Aplicar filtros
    const filterInputs = document.querySelectorAll('[data-filter-input]');
    filterInputs.forEach(input => {
        input.addEventListener('change', function() {
            console.log('🔍 Filtro aplicado:', this.name, this.value);
            // Implementar lógica de aplicação de filtro
        });
    });
};

/**
 * Inicializa sistema de ordenação
 */
const initSorting = () => {
    const sortSelect = document.querySelector('[data-sort-select]');
    
    if (sortSelect) {
        sortSelect.addEventListener('change', function() {
            const sortValue = this.value;
            console.log('📋 Ordenação selecionada:', sortValue);
            // Implementar lógica de ordenação
        });
    }
};

/**
 * Inicializa paginação
 */
const initPagination = () => {
    const paginationLinks = document.querySelectorAll('[data-pagination-link]');
    
    paginationLinks.forEach(link => {
        link.addEventListener('click', function(e) {
            const page = this.dataset.paginationLink;
            console.log('📄 Navegando para página:', page);
            // Ajax pagination pode ser implementado aqui
        });
    });
};

/**
 * Inicializa quando o DOM estiver pronto
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchPage);
} else {
    initSearchPage();
}

export default {
    init: initSearchPage
};
